import React from "react";
import SeoHead from "@/components/brand/SeoHead";
import GlowingGridBackground from "@/components/brand/GlowingGridBackground";
import GlassCard from "@/components/brand/GlassCard";
import FadeUp from "@/components/brand/FadeUp";
import ProgramSnapshot from "@/components/sections/ProgramSnapshot";
import {
    FormField,
    FormSelect,
    FormTextarea,
    HoneypotField,
    FormSubmitButton,
    FormErrorBanner,
    FormSuccessState,
} from "@/components/forms/FormControls";
import {
    useFormSubmit,
    isNonEmpty,
    isEmail,
    isPhone,
    minLength,
} from "@/components/forms/useFormSubmit";

const GRADE_OPTIONS = ["9th", "10th", "11th", "12th"];
const QUESTION_OPTIONS = [
    "Program safety",
    "Schedule",
    "Cost/sponsorship",
    "Website privacy",
    "Student application",
    "Other",
];

const PRIVACY_CARDS = [
    {
        title: "Private or unlisted by default",
        body: "Unless a parent or guardian chooses otherwise.",
        color: "#EC4899",
    },
    {
        title: "Parent / guardian chooses",
        body: "You decide whether the site is private, unlisted, or public.",
        color: "#8B5CF6",
    },
    {
        title: "Guided publishing checklist",
        body: "Students review a safety checklist before sharing anything.",
        color: "#14B8A6",
    },
];

const initialValues = {
    parentName: "",
    parentEmail: "",
    parentPhone: "",
    gradeLevel: "",
    mainQuestion: "",
    message: "",
    hp_company_website: "",
};

const validate = (v) => {
    const e = {};
    if (!isNonEmpty(v.parentName)) e.parentName = "This field is required.";
    if (!isEmail(v.parentEmail))
        e.parentEmail = "Please enter a valid email address.";
    if (!isPhone(v.parentPhone))
        e.parentPhone =
            "Please enter a valid phone number (digits, spaces, +, -, ()).";
    if (!isNonEmpty(v.gradeLevel))
        e.gradeLevel = "Please choose a grade level.";
    if (!isNonEmpty(v.mainQuestion))
        e.mainQuestion = "Please choose a main question.";
    if (!minLength(v.message, 10))
        e.message = "Please write at least 10 characters.";
    return e;
};

export default function ParentInfo() {
    const form = useFormSubmit({
        formType: "parent_info",
        initialValues,
        validate,
    });

    return (
        <>
            <SeoHead
                title="Parent Information | Vibe for Success Student Safety & Privacy"
                description="Parent information for Future Builders Lab — student safety, website privacy, parent approval, and how to ask a question."
                path="/parent-info"
            />

            {/* Hero */}
            <section
                className="vfs-section relative overflow-hidden"
                style={{ paddingTop: "clamp(2rem, 6vw, 4rem)" }}
                data-testid="parent-hero"
            >
                <GlowingGridBackground accent="#14B8A6" opacity={0.16} />
                <div className="vfs-container relative">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <span className="vfs-eyebrow">
                                Parent Information
                            </span>
                            <h1 className="vfs-h1 mt-5" data-testid="parent-h1">
                                Parent information for{" "}
                                <span className="vfs-grad-text">
                                    Future Builders Lab
                                </span>
                            </h1>
                            <p
                                className="mt-6 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                A safe, beginner-friendly program where
                                students build real digital confidence with
                                parent-approved privacy settings.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* Program snapshot */}
            <section className="vfs-section" data-testid="parent-snapshot">
                <div className="vfs-container">
                    <div className="max-w-2xl mx-auto lg:mx-0">
                        <ProgramSnapshot />
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* What students build */}
            <section className="vfs-section" data-testid="parent-build">
                <div className="vfs-container">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <h2 className="vfs-h2">
                                What students will build
                            </h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Each student builds a beginner-friendly
                                personal portfolio website with parent-approved
                                privacy settings. Websites may include an about
                                section, interests or creative work, and
                                project examples. Sensitive personal
                                information is never published.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* Student safety */}
            <section className="vfs-section" data-testid="parent-safety">
                <div className="vfs-container">
                    <FadeUp>
                        <div
                            className="vfs-glass p-7 sm:p-10 max-w-3xl"
                            style={{
                                borderRadius: 20,
                                borderColor: "rgba(20, 184, 166, 0.35)",
                            }}
                        >
                            <h2 className="vfs-h2">Student safety</h2>
                            <p
                                className="mt-5 text-base sm:text-lg leading-relaxed"
                                style={{ color: "var(--text-primary)" }}
                            >
                                Student safety and privacy are built into the
                                program. Parent or guardian approval is
                                required before participation. Personal
                                portfolio websites are private or unlisted by
                                default unless a parent or guardian chooses
                                otherwise. Students are guided not to publish
                                sensitive personal information, exact
                                addresses, school schedules, private contact
                                details, or unsafe identifying information.
                            </p>
                        </div>
                    </FadeUp>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* Website privacy cards */}
            <section
                className="vfs-section relative overflow-hidden"
                data-testid="parent-privacy"
            >
                <GlowingGridBackground accent="#8B5CF6" opacity={0.1} />
                <div className="vfs-container relative">
                    <FadeUp>
                        <h2 className="vfs-h2">Website privacy</h2>
                    </FadeUp>
                    <div className="mt-9 grid grid-cols-1 md:grid-cols-3 gap-6">
                        {PRIVACY_CARDS.map((c, i) => (
                            <FadeUp key={c.title} delay={i * 0.06}>
                                <GlassCard
                                    accentColor={c.color}
                                    className="p-7 h-full"
                                >
                                    <h3 className="vfs-h3 mb-3">{c.title}</h3>
                                    <p
                                        className="text-sm leading-relaxed"
                                        style={{
                                            color: "var(--text-secondary)",
                                        }}
                                    >
                                        {c.body}
                                    </p>
                                </GlassCard>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* Parent approval */}
            <section className="vfs-section" data-testid="parent-approval">
                <div className="vfs-container">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <h2 className="vfs-h2">Parent approval</h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Parent or guardian approval is required before
                                any student participates in Future Builders
                                Lab. You&apos;ll be contacted directly to
                                confirm approval before your student joins the
                                pilot.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            {/* Participation disclaimer */}
            <section className="vfs-section" style={{ paddingTop: 0 }}>
                <div className="vfs-container">
                    <FadeUp>
                        <div
                            className="p-6 sm:p-7 rounded-2xl max-w-3xl"
                            style={{
                                background: "rgba(251, 191, 36, 0.06)",
                                border: "1px solid rgba(251, 191, 36, 0.28)",
                            }}
                            data-testid="parent-participation-disclaimer"
                        >
                            <p
                                className="text-sm sm:text-base leading-relaxed"
                                style={{ color: "var(--text-primary)" }}
                            >
                                Future Builders Lab is an educational
                                workforce-readiness program. Participation does
                                not guarantee employment, income, admission to
                                any school or program, or professional
                                certification. The goal is to help students
                                build confidence, complete a beginner-friendly
                                digital project, and practice safe use of
                                AI-assisted web tools.
                            </p>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* Form */}
            <section
                id="parent-form"
                className="vfs-section"
                style={{ paddingTop: 0, scrollMarginTop: "6rem" }}
                data-testid="parent-form-section"
            >
                <div className="vfs-container">
                    <FadeUp>
                        <GlassCard
                            className="p-6 sm:p-10 max-w-3xl mx-auto"
                            hoverLift={false}
                        >
                            {form.success ? (
                                <FormSuccessState
                                    heading="Thank you — we'll be in touch."
                                    body={"We'll respond to your question by email. If you'd like a quicker reply, you can also email hello@vibeforsuccess.org."}
                                    onReset={form.reset}
                                    testId="parent-success"
                                />
                            ) : (
                                <form
                                    onSubmit={form.handleSubmit}
                                    noValidate
                                    data-testid="parent-form"
                                >
                                    <h2 className="vfs-h2 mb-3">
                                        Ask a question
                                    </h2>
                                    <p
                                        className="text-sm mb-7"
                                        style={{
                                            color: "var(--text-secondary)",
                                        }}
                                    >
                                        We&apos;ll respond by email as soon as
                                        we can.
                                    </p>
                                    <FormErrorBanner message={form.serverError} />
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                        <FormField
                                            id="parentName"
                                            name="parentName"
                                            label="Parent/guardian name"
                                            required
                                            autoComplete="name"
                                            maxLength={100}
                                            value={form.values.parentName}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.parentName}
                                            testId="parent-name"
                                        />
                                        <FormField
                                            id="parentEmail"
                                            name="parentEmail"
                                            type="email"
                                            label="Email"
                                            required
                                            autoComplete="email"
                                            value={form.values.parentEmail}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.parentEmail}
                                            testId="parent-email"
                                            inputMode="email"
                                        />
                                        <FormField
                                            id="parentPhone"
                                            name="parentPhone"
                                            type="tel"
                                            label="Phone"
                                            required
                                            autoComplete="tel"
                                            value={form.values.parentPhone}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.parentPhone}
                                            testId="parent-phone"
                                            inputMode="tel"
                                        />
                                        <FormSelect
                                            id="gradeLevel"
                                            name="gradeLevel"
                                            label="Student grade level"
                                            required
                                            options={GRADE_OPTIONS}
                                            value={form.values.gradeLevel}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.gradeLevel}
                                            testId="parent-grade"
                                        />
                                        <div className="sm:col-span-2">
                                            <FormSelect
                                                id="mainQuestion"
                                                name="mainQuestion"
                                                label="Main question"
                                                required
                                                options={QUESTION_OPTIONS}
                                                value={
                                                    form.values.mainQuestion
                                                }
                                                onChange={form.handleChange}
                                                onBlur={form.handleBlur}
                                                error={
                                                    form.errors.mainQuestion
                                                }
                                                testId="parent-question"
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-5">
                                        <FormTextarea
                                            id="message"
                                            name="message"
                                            label="Message"
                                            required
                                            value={form.values.message}
                                            onChange={form.handleChange}
                                            onBlur={form.handleBlur}
                                            error={form.errors.message}
                                            testId="parent-message"
                                        />
                                    </div>

                                    <HoneypotField
                                        value={form.values.hp_company_website}
                                        onChange={form.handleChange}
                                    />

                                    <div className="mt-8">
                                        <FormSubmitButton
                                            submitting={form.submitting}
                                            testId="parent-submit"
                                        >
                                            Ask a Question
                                        </FormSubmitButton>
                                    </div>
                                </form>
                            )}
                        </GlassCard>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
